import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * FilterPopover — per-column filter dropdown triggered from a header icon.
 * Shows unique values as checkboxes. Highlights when filter is active.
 *
 * @param {{
 *   columnKey: string,
 *   label: string,
 *   values: string[],
 *   selectedValues: string[],
 *   onChange: (columnKey: string, values: string[]) => void
 * }} props
 */
const FilterPopover = ({ columnKey, label, values, selectedValues, onChange }) => {
  const [open, setOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const popoverRef = useRef(null);
  const isActive = selectedValues.length > 0;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
        setFilterSearch('');
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = useCallback(
    (value) => {
      const next = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onChange(columnKey, next);
    },
    [columnKey, selectedValues, onChange]
  );

  const handleClear = useCallback(() => {
    onChange(columnKey, []);
  }, [columnKey, onChange]);

  const handleSelectAll = useCallback(() => {
    onChange(columnKey, [...values]);
  }, [columnKey, values, onChange]);

  const filteredValues = values.filter((v) =>
    v.toLowerCase().includes(filterSearch.toLowerCase())
  );

  return (
    <div className="rp-filter" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`rp-filter__trigger ${isActive ? 'is-active' : ''}`}
        aria-label={`Filter ${label}`}
        aria-expanded={open}
      >
        <i className="fe fe-filter" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="rp-filter__popover"
          role="dialog"
          aria-label={`Filter options for ${label}`}
        >
          {/* Search within filter values */}
          <div className="rp-filter__search">
            <input
              type="text"
              placeholder="Search..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="rp-filter__search-input"
              autoFocus
            />
          </div>

          {/* Select all / Clear */}
          <div className="rp-filter__actions">
            <button
              type="button"
              onClick={handleSelectAll}
              className="rp-filter__action-btn rp-filter__action-btn--select"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="rp-filter__action-btn rp-filter__action-btn--clear"
            >
              Clear
            </button>
          </div>

          {/* Checkbox list */}
          <div className="rp-filter__list">
            {filteredValues.length === 0 ? (
              <p className="rp-filter__empty">No values found</p>
            ) : (
              filteredValues.map((value) => {
                const displayValue = value || '(empty)';
                const isChecked = selectedValues.includes(value);
                return (
                  <label key={value} className="rp-filter__option">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => handleToggle(value)}
                      className="rp-checkbox"
                    />
                    <span className="rp-filter__option-text">{displayValue}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPopover;
